import { useState, useEffect, useRef, useCallback } from 'react';
import { parseCurrency, getResponseProperty } from '../../../utils/formatters';

const updateStateIfNotModified = (value, userModified, currentValue, setValue, defaultValue = '0') => {
    if (!userModified || parseCurrency(currentValue) === 0) {
        setValue(value !== null && value !== undefined && !isNaN(value) ? value : defaultValue);
    }
};

const processPayments = (parcelas, tipos) => {
    if (!Array.isArray(parcelas)) {
        return [];
    }
    const tipoList = Array.isArray(tipos) ? tipos : [tipos];
    return parcelas.filter(parcela => tipoList.includes(parcela.tipo) && parcela.valorNominal > 0);
};

export const useWalletCardLogic = (setWalletData, simulationResult) => {
    const [intermediates, setIntermediates] = useState([]);
    const [monthlyInstallmentValue, setMonthlyInstallmentValue] = useState('0');
    const [monthlyInstallmentsCount, setMonthlyInstallmentsCount] = useState('0');
    const [couponValue, setCouponValue] = useState('0');
    const [cartaDeCredito, setCartaDeCredito] = useState(false);
    const [signals, setSignals] = useState([]);
    // Ato Complementar (Sinal tipo=3, atoComplementar=true): vem calculado da API, somente leitura.
    const [atoComplementar, setAtoComplementar] = useState(0);
    const [rating, setRating] = useState('D');
    const [balaDePrata, setBalaDePrata] = useState(false);
    const [entradaFacilitada, setEntradaFacilitada] = useState(false);
    const [planoFacil, setplanoFacil] = useState(false);
    const [proposalId, setProposalId] = useState("00000000-0000-0000-0000-000000000000");

    // Flag para controlar campos modificados pelo usuário
    const [userModified, setUserModified] = useState({
        monthlyInstallmentValue: false,
        monthlyInstallmentsCount: false,
        rating: false,
        couponValue: false,
        signals: false,
        intermediates: false
    });

    // Refs para controlar atualizações
    const prevDataRef = useRef(null);
    const prevSimulationRef = useRef(null);
    const processingSignalsRef = useRef(false);
    const processingIntermediatesRef = useRef(false);
    const intermediatesRef = useRef(intermediates);
    intermediatesRef.current = intermediates;

    // Extrai funções para reduzir a complexidade
const updateMonthlyValues = (proposta) => {
        updateStateIfNotModified(
            proposta.valorRealParcelaMensal,
            userModified.monthlyInstallmentValue,
            monthlyInstallmentValue,
            setMonthlyInstallmentValue
        );

        updateStateIfNotModified(
            proposta.quantidadeParcelasMensais,
            userModified.monthlyInstallmentsCount,
            monthlyInstallmentsCount,
            setMonthlyInstallmentsCount
        );
    };

    const updateRatingAndCoupon = (proposta) => {
        if (!userModified.rating && proposta.rating) {
            setRating(proposta.rating);
        }

        if (!userModified.couponValue && proposta.cupom?.valor !== undefined) {
            setCouponValue(proposta.cupom.valor.toString() || '0');
        }
    };

    const updateCartaDeCredito = (proposta) => {
        const cartaDeCreditoValue = getResponseProperty(proposta, 'cartaDeCredito');
        if (cartaDeCreditoValue !== undefined) {
            setCartaDeCredito(Boolean(cartaDeCreditoValue));
        }
    };

    // Sinal (3) e Carta de Crédito (7) ocupam a mesma linha da tabela (o ato).
    const updateSignals = (parcelas, force = false) => {
        if ((!force && userModified.signals) || !parcelas?.length || processingSignalsRef.current) return;

        // Ato Complementar não entra na tabela editável — tratado à parte (ver updateAtoComplementar).
        const signalPayments = processPayments(parcelas, [3, 7]).filter(parcela => !parcela.atoComplementar);
        if (!signalPayments.length) return;

        processingSignalsRef.current = true;

        // Mantido como número: parseCurrency assume formato BR (ponto=milhar) e corrompe "65747.5".
        const newSignals = signalPayments.map((parcela, index) => ({
            id: `signal_${index}`,
            value: parcela.valorNominal || 0
        }));

        if (JSON.stringify(signals) !== JSON.stringify(newSignals)) {
            setSignals(newSignals);
        }

        setTimeout(() => {
            processingSignalsRef.current = false;
        }, 0);
    };

    // Sempre espelha a última simulação; não editável e fora do array `signals`.
    const updateAtoComplementar = (parcelas) => {
        if (!parcelas?.length) return;
        const parcelaAtoComplementar = parcelas.find(parcela => parcela.tipo === 3 && parcela.atoComplementar);
        setAtoComplementar(parcelaAtoComplementar?.valorNominal || 0);
    };

    const updateIntermediates = (parcelas, force = false) => {
        if ((!force && userModified.intermediates) || !parcelas?.length || processingIntermediatesRef.current) return;

        processingIntermediatesRef.current = true;

        const intermediatePayments = processPayments(parcelas, 1);
        if (intermediatePayments.length > 0) {
            const newIntermediates = intermediatePayments.map(parcela => {
                let mes = parcela.mes;
                let ano = parcela.ano;
                if ((mes == null || ano == null) && parcela.dataVencimento) {
                    const data = new Date(parcela.dataVencimento);
                    if (!Number.isNaN(data.getTime())) {
                        mes = mes ?? data.getMonth() + 1;
                        ano = ano ?? data.getFullYear();
                    }
                }
                return {
                    Valor: parcela.valorNominal || 0,
                    MesIntermediaria: mes || new Date().getMonth() + 1,
                    AnoIntermediaria: ano || new Date().getFullYear()
                };
            });

            if (JSON.stringify(intermediates) !== JSON.stringify(newIntermediates)) {
                setIntermediates(newIntermediates);
            }
        }

        setTimeout(() => {
            processingIntermediatesRef.current = false;
        }, 0);
    };

    // Efeito para atualizar campos com base no resultado da simulação
    useEffect(() => {
        const proposta = simulationResult?.proposta;
        if (!proposta) return;

        // Verifica se o resultado da simulação é novo ou igual ao anterior
        if (JSON.stringify(prevSimulationRef.current) !== JSON.stringify(simulationResult)) {
            prevSimulationRef.current = simulationResult;

            // Atualiza valores em funções separadas para reduzir complexidade
            updateMonthlyValues(proposta);
            updateRatingAndCoupon(proposta);
            updateCartaDeCredito(proposta);
            updateSignals(proposta.parcelas, true);
            updateAtoComplementar(proposta.parcelas);
            updateIntermediates(proposta.parcelas, true);

            // Se temos um ID de proposta no resultado, atualize-o sempre
            if (proposta.id) {
                setProposalId(proposta.id);
            }
        }
    }, [
        simulationResult,
        userModified.monthlyInstallmentValue,
        userModified.monthlyInstallmentsCount,
        userModified.rating,
        userModified.couponValue,
        userModified.signals,
        userModified.intermediates,
        monthlyInstallmentValue,
        monthlyInstallmentsCount,
        signals,
        intermediates,
    ]);    // Atualizar dados para o componente pai quando informações relevantes mudam
    useEffect(() => {
        if (!setWalletData) return;

        // Formatar dados para envio ao componente pai
        const prepareWalletData = () => {
            const formattedSignals = signals.map(signal => ({
                Valor: parseCurrency(signal.value) || 0
            }));

            return {
                monthlyInstallmentValue,
                monthlyInstallmentsCount,
                rating,
                balaDePrata,
                entradaFacilitada,
                cartaDeCredito,
                planoFacil,
                couponValue,
                signals: formattedSignals,
                intermediates,
                idPropostaAnaliseCredito: proposalId
            };
        };

        const currentData = prepareWalletData();

        // Compare com o estado anterior antes de atualizar
        const currentJSON = JSON.stringify(currentData);
        const previousJSON = JSON.stringify(prevDataRef.current);

        if (previousJSON !== currentJSON) {
            setWalletData(currentData);
            prevDataRef.current = { ...currentData };
        }
    }, [
        monthlyInstallmentValue,
        monthlyInstallmentsCount,
        rating,
        balaDePrata,
        entradaFacilitada,
        cartaDeCredito,
        planoFacil,
        couponValue,
        signals,
        intermediates,
        proposalId,
        setWalletData
    ]);

    const handleProposalIdChange = (newValue) => {
        setProposalId(newValue);
    };

    // Handlers para atualização dos estados    // Função genérica para comparar e atualizar estados
    const updateIfChanged = (currentValue, newValue, setValue, field, process = false) => {
        if (process) return;

        if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
            setValue(newValue);
            return true;
        }
        return false;
    };

    // Função genérica para marcar campo como modificado pelo usuário
    const markAsUserModified = (field, condition) => {
        if (condition) {
            setUserModified(prev => ({ ...prev, [field]: true }));
        }
    };

    const handleIntermediatesChange = useCallback((newIntermediates) => {
        if (processingIntermediatesRef.current) return;

        if (JSON.stringify(intermediatesRef.current) === JSON.stringify(newIntermediates)) {
            return;
        }

        setIntermediates(newIntermediates);

        const hasValue = newIntermediates.some(parcela => parseFloat(parcela.Valor || 0) > 0);
        if (hasValue) {
            setUserModified(prev => (prev.intermediates ? prev : { ...prev, intermediates: true }));
        }
    }, []);

    // Handlers para valores numéricos: generic handler para reduzir duplicação
    const handleNumericValueChange = (e, setValue, fieldName) => {
        const newValue = e.target.value;
        setValue(newValue);

        // Marca como modificado pelo usuário se for um valor válido e não-zero
        const isModified = fieldName === 'monthlyInstallmentsCount'
            ? parseInt(newValue) > 0
            : parseCurrency(newValue) > 0;

        markAsUserModified(fieldName, isModified);
    };

    // Handlers simplificados usando as funções genéricas
    const handleMonthlyInstallmentValueChange = (e) => {
        handleNumericValueChange(e, setMonthlyInstallmentValue, 'monthlyInstallmentValue');
    };

    const handleMonthlyInstallmentsCountChange = (e) => {
        handleNumericValueChange(e, setMonthlyInstallmentsCount, 'monthlyInstallmentsCount');
    };

    const handleCouponValueChange = (e) => {
        handleNumericValueChange(e, setCouponValue, 'couponValue');
    };

    const handleSignalsChange = (newSignals) => {
        // Evitar processamento se já estiver atualizando sinais da simulação
        if (processingSignalsRef.current) return;

        const hasChanged = updateIfChanged(signals, newSignals, setSignals);
        if (hasChanged) {
            const hasValue = newSignals.some(signal => parseCurrency(signal.value || 0) > 0);
            markAsUserModified('signals', hasValue);
        }
    };

    const handleRatingChange = (e) => {
        const newValue = e.target.value;
        setRating(newValue);
        markAsUserModified('rating', true);
    };

    // Toggle handlers - mantidos simples
    const handleBalaDePrataChange = () => {
        const newValue = !balaDePrata;
        setBalaDePrata(newValue);
        if (newValue) {
            setEntradaFacilitada(false);
        }
    };

    const handleEntradaFacilitadaChange = () => {
        const newValue = !entradaFacilitada;
        setEntradaFacilitada(newValue);
        if (newValue) {
            setBalaDePrata(false);
        }
    };

    const handleCartaDeCreditoChange = () => {
        setCartaDeCredito(!cartaDeCredito);
    };

    const handleplanoFacilChange = () => {
        setplanoFacil(!planoFacil);
    };

    const calculateTotalSignal = () => {
        return signals.reduce((sum, signal) => sum + parseCurrency(signal.value || 0), 0);
    };

    return {
        monthlyInstallmentValue,
        monthlyInstallmentsCount,
        couponValue,
        cartaDeCredito,
        signals,
        atoComplementar,
        rating,
        balaDePrata,
        entradaFacilitada,
        planoFacil,
        intermediates,
        handleIntermediatesChange,
        handleMonthlyInstallmentValueChange,
        handleMonthlyInstallmentsCountChange,
        handleCouponValueChange,
        handleSignalsChange,
        handleRatingChange,
        handleBalaDePrataChange,
        handleEntradaFacilitadaChange,
        handleCartaDeCreditoChange,
        handleplanoFacilChange,
        handleProposalIdChange,
        calculateTotalSignal
    };
};